import { GlobalOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import Icon from '@ant-design/icons/lib/components/Icon';
import { EMessengerChatSourceType } from '@xometry/ui';
import { Space, Tooltip } from 'antd';
import { ReactComponent as EmulateIcon } from 'assets/images/icons/emulate.min.svg';
import { MessengerEntryPoint } from 'collections/amplitudeEvents';
import { AntButton } from 'components/ant/AntButton/AntButton';
import Mailer from 'components/Mailer';
import { MessengerButton } from 'components/shared/MessengerButton';
import { OrganizationIcon } from 'components/UI/Icons/OrganizationIcon';
import { Phone } from 'components/UI/Phone';
import { Status, StatusType } from 'components/UI/Status';
import { shortName } from 'components/utils/formatters';
import { IProvider } from 'interfaces/graphql';
import React, { FC, useState } from 'react';
import { routesApi } from 'routes/api';
import useRedirect from 'utils/hooks/useRedirect';

import s from './ContactInfo.module.less';

interface Props {
  provider: IProvider;
}

export const ContactInfo: FC<Props> = ({ provider }) => {
  const redirect = useRedirect();
  const [mailerVisible, setMailerVisible] = useState<boolean>(false);

  const primaryContact = provider.contacts.find((contact) => contact.id === provider.primaryContactId);

  const phone = primaryContact?.phone || provider.phone;
  const name = shortName(
    (primaryContact ? `${primaryContact.firstName} ${primaryContact.lastName}` : provider.name) || '',
    24,
  );

  const impersonate = () => {
    if (!primaryContact) {
      return;
    }

    void redirect(routesApi.providerContactImpersonatePath(provider.id, primaryContact.id));
  };

  return (
    <div className={s.wrapper}>
      <div className={s.itemWrapper}>
        <Space size={4}>
          <OrganizationIcon className={s.icon} />
          <span>{provider.legalName}</span>
        </Space>

        {provider.website ? (
          <a
            href={provider.website.indexOf('//') ? provider.website : `//${provider.website}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <GlobalOutlined className={s.webSiteIcon} />
          </a>
        ) : null}
      </div>
      <div className={s.itemWrapper}>
        <Space size={4}>
          <UserOutlined className={s.icon} />
          <span>{name}</span>
          {provider.locale ? (
            <Status shape="square" type={StatusType.Gray} uppercase className={s.localeTag}>
              {provider.locale}
            </Status>
          ) : null}
        </Space>
      </div>
      <div>
        {phone ? <Phone value={phone} /> : null}{' '}
        <Tooltip title="Messages">
          <MessengerButton
            context={{
              id: String(provider.id),
              sourceType: EMessengerChatSourceType.PARTNER,
              sourceId: String(provider.id),
              title: name || '',
            }}
            height="16px"
            className={s.messageCenterButton}
            entryPoint={MessengerEntryPoint.DEAL_HEADER}
          />
        </Tooltip>
        <Tooltip title="Emulate PA">
          <AntButton type="link" icon={<Icon component={EmulateIcon} />} size="small" onClick={impersonate} />
        </Tooltip>
        <Tooltip title="Send Email">
          <AntButton
            type="link"
            htmlType="button"
            size="small"
            onClick={() => setMailerVisible(true)}
            icon={<MailOutlined />}
          />
        </Tooltip>
      </div>
      <Mailer
        title="General mailer"
        visible={mailerVisible}
        requests={{
          fetch: routesApi.providerEmailTemplates(String(provider.id)),
          submit: routesApi.providerSendEmail(String(provider.id)),
          directSubmit: routesApi.providerSendDirectEmail(String(provider.id)),
          uploadFile: routesApi.entityAttachmentsUploadPath('providers', provider.id),
        }}
        onCancel={() => setMailerVisible(false)}
      />
    </div>
  );
};
